import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { TrusteeApproveBatchModalProps } from './TrusteeApproveBatchModal.types'

export function TrusteeApproveBatchModal({
  isOpen,
  onClose,
  disbursementBatchId,
  refreshData
}: Readonly<TrusteeApproveBatchModalProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()

  const [status, setStatus] = useState<'error' | 'success' | null>(null)

  const [{ isPending }, { mutateAsync: trusteeApproveBatch }] = useMutation(disbursementsService.trusteeApproveBatch)

  const handleClose = () => {
    if (isPending) {
      return
    }

    setStatus(null)
    refreshData()
    onClose()
  }

  const handleSubmit = async () => {
    try {
      await trusteeApproveBatch(disbursementBatchId)
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  const successModal = status === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.trusteeApproveBatch.success.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="trustee-approve-batch-success-modal"
      buttonTestId="trustee-approve-batch-success-modal-close"
    />
  )

  const errorModal = status === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.trusteeApproveBatch.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="trustee-approve-batch-error-modal"
      buttonTestId="trustee-approve-batch-error-modal-close"
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.trusteeApproveBatch.title')}
          primaryButtonText={isPending ? t('button.approving') : t('button.approveAll')}
          primaryBtnTestId="trustee-approve-batch-modal-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="trustee-approve-batch-modal-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isPending}
        >
          <Text as="p" size="small" color="gray600">
            {t('dialog.trusteeApproveBatch.description')}
          </Text>
        </ActionModal>
      )}
    </Modal>
  )
}
